import type { ComponentProps } from 'react';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { ButtonGroupItem } from './ButtonGroupItem';

// ─────────────────────────────────────────────────────────────────────────────

export const ButtonGroup = (props: ButtonGroupProps) => {
  const {
    value,
    items,
    size = 'medium',
    gap = 'default',
    fluid,
    itemsHorizontalPadding = 'medium',
    onChange,
  } = props;

  if (items.length === 0) return null;

  return (
    <View style={styles.container} accessibilityRole="radiogroup">
      {items.map((item, index) => {
        const { value: itemValue, label, helperText, accessibilityHint } = item;

        const isActive = itemValue === value;
        const isLast = index === items.length - 1;

        return (
          <ButtonGroupItem
            key={itemValue}
            value={itemValue}
            setValue={onChange}
            label={label}
            helperText={helperText}
            isActive={isActive}
            size={size}
            accessibilityHint={accessibilityHint}
            gap={isLast ? undefined : gap}
            fluid={fluid}
            horizontalPadding={itemsHorizontalPadding}
          />
        );
      })}
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ButtonRadioGroupItemProps = ComponentProps<typeof ButtonGroupItem>;

type ButtonGroupProps = {
  readonly value: string;
  readonly items: readonly ButtonGroupItemData[];
  readonly onChange: (value: string) => void;

  /** @default "medium" */
  readonly itemsHorizontalPadding?: ButtonRadioGroupItemProps['horizontalPadding'];

  /** @default medium */
  readonly size?: ButtonRadioGroupItemProps['size'];

  /** @default "default" */
  readonly gap?: ButtonRadioGroupItemProps['gap'];

  /** @default false */
  readonly fluid?: ButtonRadioGroupItemProps['fluid'];
};

type ButtonGroupItemData = {
  readonly value: string;
  readonly label: string;
  readonly accessibilityHint?: string;
  readonly helperText?: string;
};
